/**
 * <our-team-grid>
 * 1. Staggered reveal: toggles `revealed` once the grid scrolls into view.
 *    Per-card stagger is driven by the CSS var `--reveal-index` (set in Liquid).
 * 2. Column drift: while the grid is on screen, sets `--scroll-shift` (px) from
 *    scroll progress so alternating columns drift apart as you scroll.
 *    Runs on mobile + desktop; disabled for reduced-motion or section toggle off.
 */
if (!customElements.get('our-team-grid')) {
  customElements.define(
    'our-team-grid',
    class OurTeamGrid extends HTMLElement {
      connectedCallback() {
        this.reduceMotion =
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.disabled = this.hasAttribute('data-no-animate') || this.reduceMotion;
        this.amplitude = parseFloat(this.dataset.parallax) || 0;

        this.setupReveal();
        this.setupParallax();
      }

      /* ---- Reveal ------------------------------------------------ */
      setupReveal() {
        if (this.disabled || !('IntersectionObserver' in window)) {
          this.classList.add('revealed');
          return;
        }

        this.revealObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('revealed');
                observer.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
        );
        this.revealObserver.observe(this);
      }

      /* ---- Parallax ---------------------------------------------- */
      setupParallax() {
        if (this.disabled || this.amplitude <= 0) return;

        this.ticking = false;
        this.inView = false;

        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          window.requestAnimationFrame(() => {
            this.updateShift();
            this.ticking = false;
          });
        };

        // Only listen while the grid is near the viewport.
        this.viewportObserver = new IntersectionObserver(
          (entries) => {
            this.inView = entries[0].isIntersecting;
            if (this.inView) {
              window.addEventListener('scroll', this.onScroll, { passive: true });
              this.updateShift();
            } else {
              window.removeEventListener('scroll', this.onScroll);
            }
          },
          { rootMargin: '20% 0px 20% 0px' }
        );
        this.viewportObserver.observe(this);

        this.onResize = () => this.updateShift();
        window.addEventListener('resize', this.onResize, { passive: true });
        this.updateShift();
      }

      updateShift() {
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const rect = this.getBoundingClientRect();
        const gridCenter = rect.top + rect.height / 2;
        // progress ~ -1 (below) .. +1 (above) as the grid crosses the viewport
        let progress = (vh / 2 - gridCenter) / vh;
        progress = Math.max(-1, Math.min(1, progress * 2));
        const shift = (progress * this.amplitude).toFixed(1);
        this.style.setProperty('--scroll-shift', shift + 'px');
      }

      disconnectedCallback() {
        if (this.revealObserver) this.revealObserver.disconnect();
        if (this.viewportObserver) this.viewportObserver.disconnect();
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
      }
    }
  );
}
